import { BookOpen, ExternalLink, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { SCIENTIFIC_BASIS } from "@/lib/scientificBasis";

export default function ScientificDialog({ open, onClose, gameKey, themeColor = "#38BDF8" }) {
  const { language } = useGame();
  const data = SCIENTIFIC_BASIS[gameKey];
  if (!data) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={onClose}
          data-testid="scientific-dialog-overlay"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, y: 30 }}
            transition={{ type: "spring", damping: 20 }}
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-8 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="scientific-dialog"
          >
            <div
              className="p-5 pb-4 flex items-center justify-between text-white"
              style={{ background: themeColor }}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-6 h-6" strokeWidth={3} />
                <h3 className="font-display font-bold text-xl sm:text-2xl">
                  {language === "id" ? "Dasar Ilmiah" : "Scientific Basis"}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-white/30 hover:bg-white/50 flex items-center justify-center"
                data-testid="scientific-dialog-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                  {language === "id" ? "Domain" : "Domain"}
                </div>
                <div className="font-display font-bold text-base" style={{ color: themeColor }}>
                  {data.domain[language]}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                  {language === "id" ? "Teori" : "Theory"}
                </div>
                <div className="font-bold text-slate-700">{data.theory[language]}</div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                  {language === "id" ? "Penjelasan" : "Rationale"}
                </div>
                <p className="text-slate-700 leading-relaxed">{data.rationale[language]}</p>
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  {language === "id" ? "Referensi Jurnal" : "Journal References"}
                </div>
                <ul className="space-y-3">
                  {data.references.map((ref, i) => (
                    <li
                      key={`${ref.authors}-${ref.year}`}
                      className="p-3 rounded-2xl bg-slate-50 border-l-4"
                      style={{ borderColor: themeColor }}
                      data-testid={`reference-${i}`}
                    >
                      <div className="font-bold text-sm text-slate-800">
                        {ref.authors} ({ref.year})
                      </div>
                      <div className="italic text-sm text-slate-600 mt-0.5">{ref.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{ref.journal}</div>
                      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-bold text-white"
                          style={{ background: themeColor }}
                        >
                          {ref.index}
                        </span>
                        {ref.url && (
                          <a
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-bold hover:underline"
                            style={{ color: themeColor }}
                            data-testid={`reference-link-${i}`}
                          >
                            {language === "id" ? "Buka sumber" : "Open source"}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-xs text-slate-400 italic pt-2 border-t">
                {language === "id"
                  ? "ⓘ Verifikasi sitasi via portal SINTA atau Scopus sebelum penggunaan akademik formal."
                  : "ⓘ Verify citations via SINTA or Scopus portal before formal academic use."}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
